import { useCallback, useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { subsetFormSchema, SubsetFormData } from './subset-form-schema';
import { SubsetSuggestionStatus } from '../../../../../../../../backend/src/matter/subset/subset-suggestion/subset-suggestion.entity';
import { MatterDocsFilters } from '@/../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useCreateDocSubset } from '@/api/mutations/useCreateDocSubset';
import { useUpdateSubset } from '@/api/mutations/useUpdateSubset';
import { useUpdateSuggestionStatus } from '@/api/mutations/useUpdateSuggestionStatus';
import { useGetDocs, useGetDocsCount, useGetDocIds } from '@/api/queries/useGetDocs';
import { useGetMatterDocFilterValues } from '@/api/queries/useGetMatterDocFilterValues';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetFilterOptions } from '@/api/queries/useGetMetadataFilterOptions';
import { useGetProcessingDocs } from '@/api/queries/useGetProcessingDocs';
import { useGetSubsets, hasRecentProcessingSubset } from '@/api/queries/useGetSubsets';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Popover, PopoverTrigger, PopoverContent } from '@/new/components/shadcn/popover';
import { Switch } from '@/new/components/shadcn/switch';
import { Textarea } from '@/new/components/shadcn/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';
import { DocPickerTable } from '@/new/pages/matters/:matterId/subsets/new/components/doc-picker-table';

interface SubsetFormProps {
  mode: 'create' | 'edit';
  matterId: string;
  subsetId?: string;
  initialData?: {
    name: string;
    legalIssues: string;
    selectedDocIds: string[];
    baseDocIds?: string[];
  };
  suggestionId?: string;
  isLoadingInitialData?: boolean;
  onSuccess?: (subsetId: string) => void;
  showIncludedByDefault?: boolean;
}

export type ServerFilters = Omit<MatterDocsFilters, 'searchQuery'>;

export function SubsetForm({
  mode,
  matterId,
  subsetId,
  initialData,
  suggestionId,
  isLoadingInitialData,
  onSuccess,
  showIncludedByDefault,
}: SubsetFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: matter } = useGetMatterV2(matterId);
  const matterIssues = matter?.issues;

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{ id: 'file.name', desc: false }]);

  // Server-side filter state (without docIds - that's handled separately for "show included")
  const [serverFilters, setServerFilters] = useState<ServerFilters>({});

  // Search query state (separate from filters)
  const [searchQuery, setSearchQuery] = useState('');

  // "Show Included" state - when true, only show selected documents
  const [showIncluded, setShowIncluded] = useState(!!showIncludedByDefault);

  const { data: subsets } = useGetSubsets(matterId);
  const { data: processingDocsData } = useGetProcessingDocs(matterId);
  const createSubset = useCreateDocSubset();
  const updateSubset = useUpdateSubset();
  const updateSuggestionStatus = useUpdateSuggestionStatus();

  const defaultLegalIssues = initialData?.legalIssues ?? (mode === 'create' && matterIssues ? matterIssues : '');

  const form = useForm<SubsetFormData>({
    resolver: zodResolver(subsetFormSchema),
    defaultValues: {
      name: initialData?.name ?? '',
      legalIssues: defaultLegalIssues,
    },
  });

  // "Apply Matter Issues" toggle - default to true when matter issues exist and creating
  const [applyMatterIssues, setApplyMatterIssues] = useState(mode === 'create' && !!matterIssues);

  // Enable the toggle when matter issues load asynchronously in create mode
  useEffect(() => {
    if (mode === 'create' && matterIssues) {
      setApplyMatterIssues(true);
    }
  }, [mode, matterIssues]);

  // Sync form value when applyMatterIssues is toggled
  useEffect(() => {
    if (applyMatterIssues && matterIssues) {
      form.setValue('legalIssues', matterIssues, { shouldValidate: true });
    } else {
      form.setValue('legalIssues', defaultLegalIssues, { shouldValidate: true });
    }
  }, [applyMatterIssues, matterIssues]);

  // Create a Set of processing document IDs for efficient lookup
  const processingDocIds = useMemo(() => new Set(processingDocsData?.docIds ?? []), [processingDocsData?.docIds]);
  const hasProcessingSubset = hasRecentProcessingSubset(subsets);

  // Find the all docs case ID for NL filtering since for now we need that to build NL filters
  const allDocumentsCase = useMemo(() => {
    return subsets?.find((subset) => subset.isAllDocuments);
  }, [subsets]);

  // Fetch filter options for dynamic columns (Relativity and Wexler fields)
  const { data: filterOptions } = useGetFilterOptions({ matterId });

  // Fetch distinct filter values for document filters (types, extensions, issues, persons)
  const { data: docFilterValues } = useGetMatterDocFilterValues(matterId);

  // Confirmation dialog state for edit mode
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Watch form values for change detection
  const watchedName = form.watch('name');
  const watchedLegalIssues = form.watch('legalIssues');

  // Check if values have changed from initial
  const nameHasChanged = useMemo(() => watchedName !== (initialData?.name ?? ''), [watchedName, initialData?.name]);
  const issuesHaveChanged = useMemo(
    () => watchedLegalIssues !== (initialData?.legalIssues ?? ''),
    [watchedLegalIssues, initialData?.legalIssues],
  );

  // Initialize row selection from initial data
  const [rowSelection, setRowSelection] = useState<RowSelectionState>(() => {
    if (initialData?.selectedDocIds) {
      return initialData.selectedDocIds.reduce((acc, docId) => {
        acc[docId] = true;
        return acc;
      }, {} as RowSelectionState);
    }
    return {};
  });

  // Update row selection when initial data changes
  useEffect(() => {
    if (initialData?.selectedDocIds) {
      setRowSelection(
        initialData.selectedDocIds.reduce((acc, docId) => {
          acc[docId] = true;
          return acc;
        }, {} as RowSelectionState),
      );
    }
  }, [initialData?.selectedDocIds]);

  // Track initial document IDs for edit mode comparison
  // Use baseDocIds (pre-merge) when available, so added docs from additionalDocIds show as changes
  const initialDocIds = useMemo(() => initialData?.baseDocIds ?? initialData?.selectedDocIds ?? [], [initialData]);

  // Get selected doc IDs for "show included" filtering
  const selectedDocIds = useMemo(() => Object.keys(rowSelection), [rowSelection]);

  // Compute the effective filters including docIds for "show included" and searchQuery
  const filters = useMemo(() => {
    const baseFilters: MatterDocsFilters = { ...serverFilters };
    if (showIncluded) {
      baseFilters.docIds = selectedDocIds.length > 0 ? selectedDocIds : [];
    }
    if (searchQuery.trim()) {
      baseFilters.searchQuery = searchQuery.trim();
    }
    return baseFilters;
  }, [serverFilters, showIncluded, selectedDocIds, searchQuery]);

  const {
    data: responseDocs,
    isLoading: isLoadingDocs,
    isFetching: isFetchingDocs,
  } = useGetDocs({
    matterId,
    subsetId: allDocumentsCase?.id ?? '',
    page: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filters,
    sortBy: sorting?.[0]?.id?.split('.').pop() ?? undefined,
    sortOrder: sorting?.[0]?.desc ? 'DESC' : 'ASC',
    isFlattened: true,
  });

  const { data: docsCountData } = useGetDocsCount({
    matterId,
    subsetId: allDocumentsCase?.id ?? '',
    pageSize: pagination.pageSize,
    filters,
    isFlattened: true,
  });

  // Track whether user has attempted to select/deselect all - enables lazy fetching
  const [hasAttemptedSelectAll, setHasAttemptedSelectAll] = useState(false);

  const {
    data: filteredDocIdsData,
    refetch: refetchFilteredDocIds,
    isFetching: isFetchingFilteredDocIds,
  } = useGetDocIds({
    matterId,
    subsetId: allDocumentsCase?.id ?? '',
    filters,
    isFlattened: true,
    enabled: hasAttemptedSelectAll,
  });

  const selectedCount = useMemo(() => Object.keys(rowSelection).length, [rowSelection]);

  // Calculate document changes for edit mode
  const documentChanges = useMemo(() => {
    if (mode !== 'edit') return { added: 0, removed: 0 };

    const addedDocs = selectedDocIds.filter((id) => !initialDocIds.includes(id));
    const removedDocs = initialDocIds.filter((id) => !selectedDocIds.includes(id));

    return {
      added: addedDocs.length,
      removed: removedDocs.length,
    };
  }, [mode, selectedDocIds, initialDocIds]);

  // Check if any changes have been made in edit mode
  const hasChanges = useMemo(() => {
    if (mode !== 'edit' || !initialData) return true;

    const nameChanged = watchedName !== initialData.name;
    const issuesChanged = watchedLegalIssues !== initialData.legalIssues;
    const docsChanged = documentChanges.added > 0 || documentChanges.removed > 0;

    return nameChanged || issuesChanged || docsChanged;
  }, [mode, initialData, watchedName, watchedLegalIssues, documentChanges]);

  // Handler for selecting all filtered documents
  const handleSelectAllFiltered = useCallback(async () => {
    setHasAttemptedSelectAll(true);

    try {
      // Use cached data if available, otherwise fetch
      let docIds = filteredDocIdsData?.docIds;

      if (!docIds) {
        const result = await refetchFilteredDocIds();
        docIds = result.data?.docIds;
      }

      if (docIds) {
        // Filter out processing docs
        const selectableDocIds = docIds.filter((id) => !processingDocIds.has(id));

        const newSelection = selectableDocIds.reduce((acc, docId) => {
          acc[docId] = true;
          return acc;
        }, {} as RowSelectionState);

        setRowSelection((prev) => ({ ...prev, ...newSelection }));
      }
    } catch (error) {
      console.error('Failed to fetch filtered doc IDs:', error);
    }
  }, [filteredDocIdsData, refetchFilteredDocIds, processingDocIds]);

  // Handler for deselecting all
  const handleDeselectAll = useCallback(async () => {
    // Check if filters and search are empty
    const hasFilters = Object.keys(serverFilters).length > 0;
    const hasSearch = searchQuery.trim().length > 0;

    if (!hasFilters && !hasSearch) {
      // No filters or search - remove all selections
      setRowSelection({});
    } else {
      // Has filters or search - remove only filtered documents
      setHasAttemptedSelectAll(true);

      try {
        // Use cached data if available, otherwise fetch
        let docIds = filteredDocIdsData?.docIds;

        if (!docIds) {
          const result = await refetchFilteredDocIds();
          docIds = result.data?.docIds;
        }

        if (docIds) {
          setRowSelection((prev) => {
            const updated = { ...prev };
            // Remove all filtered doc IDs from selection
            docIds.forEach((docId) => {
              delete updated[docId];
            });
            return updated;
          });
        }
      } catch (error) {
        console.error('Failed to fetch filtered doc IDs:', error);
      }
    }
  }, [serverFilters, searchQuery, filteredDocIdsData, refetchFilteredDocIds]);

  // Handler for server filter changes (from DocPickerTable)
  const handleServerFiltersChange = useCallback((filters: ServerFilters) => {
    setServerFilters(filters);
    // Reset to first page when filters change
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Handler for "show included" toggle
  const handleShowIncludedChange = useCallback((value: boolean) => {
    setShowIncluded(value);
    // Reset to first page when toggling show included
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  // Handler for search query changes
  const handleSearchQueryChange = useCallback((query: string) => {
    setSearchQuery(query);
    // Reset to first page when search query changes
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const handleButtonClick = form.handleSubmit(() => {
    // Don't proceed if no docs selected
    if (selectedDocIds.length === 0) return;

    // Show confirmation dialog for edit mode, otherwise submit directly
    if (mode === 'edit') {
      setShowConfirmDialog(true);
    } else {
      handleSubmit();
    }
  });

  const handleSubmit = async () => {
    const formValues = form.getValues();

    try {
      let result: { id: string } | undefined = undefined;

      if (mode === 'create') {
        result = await createSubset.mutateAsync({
          matterId: matterId,
          body: {
            title: formValues.name,
            legalIssues: formValues.legalIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset creation
        if (result) {
          trackCustomEvent(TrackingEventsV2.SubsetCreate, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
            usingMatterIssues: applyMatterIssues,
          });
        }
      } else {
        result = await updateSubset.mutateAsync({
          matterId,
          subsetId: subsetId!,
          body: {
            name: formValues.name,
            legalIssues: formValues.legalIssues,
            docIds: selectedDocIds,
          },
        });

        // Track subset update
        if (result) {
          trackCustomEvent(TrackingEventsV2.SubsetEdit, {
            matterId,
            subsetId: result.id,
            documentCount: selectedDocIds.length,
            docsAdded: documentChanges.added,
            docsRemoved: documentChanges.removed,
          });
        }
      }

      // Mark suggestion as accepted if this was created from a suggestion
      if (result && mode === 'create' && suggestionId) {
        await updateSuggestionStatus.mutateAsync({
          suggestionId,
          status: 'accepted' as SubsetSuggestionStatus.Accepted, // enum can't be used directly on FE - don't use enums in future
        });
      }

      if (result && onSuccess) {
        onSuccess(result.id);
      } else if (result) {
        navigate(`/matters/${matterId}/subsets/${result.id}`);
      }

      // Close the dialog if it was open
      setShowConfirmDialog(false);
    } catch (error) {
      console.error(`Failed to ${mode} subset:`, error);
    }
  };

  const isLoading = mode === 'create' ? createSubset.isLoading : updateSubset.isLoading;
  const buttonText = mode === 'create' ? 'Create Subset' : 'Update Subset';
  const loadingText = mode === 'create' ? 'Creating...' : 'Updating...';

  return (
    <PageContainer className="flex h-full flex-col overflow-hidden px-0">
      <div className="flex grow overflow-hidden">
        <section className="flex flex-col">
          <div className="flex items-center gap-4 p-7 pr-5">
            <PageTitle text={mode === 'create' ? 'Create Subset' : 'Edit Subset'} iconClass="lucide--group" />

            <Popover>
              <PopoverTrigger>
                <Icon className="lucide--info mt-1.25 text-base text-gray-400 hover:text-gray-600" />
              </PopoverTrigger>

              <PopoverContent side="right" sideOffset={14} className="mt-4 text-gray-800">
                <div className="px-1">
                  Subsets allow you to easily {t('analyse')} facts from a select group of documents.
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <Form {...form}>
            <form
              className="m-2 mt-0 flex w-sm grow flex-col justify-between overflow-y-hidden rounded-md border bg-gray-50 shadow-lg/2"
              data-tour="subset-form"
              onSubmit={handleButtonClick}
            >
              <div className="flex grow flex-col justify-between overflow-y-auto">
                <div className="flex flex-col gap-2 px-5 py-2">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <div className="my-3 flex items-center gap-1">
                          <FormLabel required className="mb-0 flex items-center gap-2 px-1">
                            <p className="text-gray-700">Subset Name</p>
                          </FormLabel>

                          {nameHasChanged && (
                            <button
                              type="button"
                              onClick={() => {
                                form.setValue('name', initialData?.name ?? '', { shouldValidate: true });
                              }}
                              className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                              title="Undo changes"
                            >
                              <Icon className="lucide--rotate-ccw text-sm" />
                            </button>
                          )}
                        </div>

                        <FormControl>
                          <Input type="text" placeholder="John Doe's Emails" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {matterIssues && (
                    <div className="my-4 flex items-center gap-4 pl-1.5">
                      <Switch
                        id="use-matter-issues"
                        checked={applyMatterIssues}
                        onCheckedChange={setApplyMatterIssues}
                      />
                      <label htmlFor="use-matter-issues" className="flex cursor-pointer flex-col space-y-0.5">
                        <span className="text-sm font-medium text-gray-700">Apply Matter issues</span>
                        <span className="text-xs text-gray-500">
                          Fact relevance will match the Matter-level classification.
                        </span>
                      </label>
                    </div>
                  )}

                  <FormField
                    control={form.control}
                    name="legalIssues"
                    render={({ field }) => (
                      <FormItem className={cn(applyMatterIssues && 'opacity-50')}>
                        <div className="my-3 flex items-center gap-1">
                          <FormLabel required className="mb-0 flex items-center gap-2 px-1">
                            <p className="text-gray-700">Subset Issues</p>
                          </FormLabel>

                          {issuesHaveChanged && !applyMatterIssues && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                form.setValue('legalIssues', initialData?.legalIssues ?? '', { shouldValidate: true });
                              }}
                              className="animate-in fade-in w-fit text-gray-400 duration-100 hover:text-gray-600"
                              title="Undo changes"
                            >
                              <Icon className="lucide--rotate-ccw text-sm" />
                            </button>
                          )}
                        </div>
                        <p className="my-2 mb-4 px-1 text-gray-500">
                          Subset-specific issues are used to determine the relevance classification of facts.
                        </p>
                        <FormControl>
                          <Textarea
                            placeholder="John Doe's awareness of fraudulent activity"
                            rows={14}
                            {...field}
                            disabled={applyMatterIssues}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {mode === 'create' && (
                  <p className="flex items-center gap-3 p-5 pr-6 text-balance text-gray-500">
                    <Icon className="lucide--info mb-0.75 text-gray-400" />
                    You can always add or remove documents from a Subset after creation.
                  </p>
                )}
              </div>

              <div className="rounded-b-md border-t bg-white p-4">
                {selectedCount > 20000 && (
                  <div className="mb-3 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-700">
                    <Icon className="lucide--triangle-alert mt-1" />
                    We're currently working on stability improvements for large subsets. You may experience issues when
                    trying to select more than 20,000 documents.
                  </div>
                )}

                <div className="flex w-full items-center justify-between">
                  {mode === 'edit' && (documentChanges.added > 0 || documentChanges.removed > 0) ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Icon className="lucide--file h-3.5 w-3.5 text-gray-400" />
                      <span>
                        {documentChanges.added > 0 && (
                          <span className="text-green-600">+{documentChanges.added} added</span>
                        )}
                        {documentChanges.added > 0 && documentChanges.removed > 0 && <span>, </span>}
                        {documentChanges.removed > 0 && (
                          <span className="text-red-600">-{documentChanges.removed} removed</span>
                        )}
                      </span>
                    </div>
                  ) : (
                    <div />
                  )}

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={mode === 'create' && hasProcessingSubset ? 0 : undefined}>
                        <Button
                          type="submit"
                          variant="primary"
                          disabled={
                            !selectedCount || isLoading || !hasChanges || (mode === 'create' && hasProcessingSubset)
                          }
                        >
                          <Icon className={cn(isLoading ? 'lucide--loader-circle animate-spin' : 'lucide--group')} />
                          <span>
                            {isLoading
                              ? loadingText
                              : mode === 'create'
                                ? `${buttonText} with ${selectedCount} Document${selectedCount === 1 ? '' : 's'}`
                                : buttonText}
                          </span>
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {mode === 'create' && hasProcessingSubset && (
                      <TooltipContent>
                        Please wait for existing Subsets to finish being created before creating a new Subset.
                      </TooltipContent>
                    )}
                  </Tooltip>
                </div>
              </div>
            </form>
          </Form>
        </section>

        <DocPickerTable
          docs={responseDocs?.docs}
          deduplicatedDocIds={responseDocs?.deduplicatedDocIds}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          allDocumentsCaseId={allDocumentsCase?.id ?? ''}
          matterId={matterId}
          mode={mode}
          initialDocIds={mode === 'edit' ? initialDocIds : undefined}
          isLoadingInitialSelection={isLoadingInitialData}
          isLoadingDocs={isLoadingDocs || isFetchingDocs}
          processingDocIds={processingDocIds}
          relativityFields={filterOptions?.relativityFields?.fields}
          relativityDistinctValues={filterOptions?.relativityFields?.availableOptions}
          wexlerFields={filterOptions?.wexlerFields?.fields}
          wexlerDistinctValues={filterOptions?.wexlerFields?.availableOptions}
          docFilterOptions={docFilterValues}
          // Server-side pagination props
          pagination={pagination}
          onPaginationChange={setPagination}
          pageCount={docsCountData?.pageCount ?? 0}
          totalCount={docsCountData?.totalCount ?? 0}
          // Server-side sorting props
          sorting={sorting}
          onSortingChange={setSorting}
          // Server-side filter props
          serverFilters={serverFilters}
          onServerFiltersChange={handleServerFiltersChange}
          // Search query props
          searchQuery={searchQuery}
          onSearchQueryChange={handleSearchQueryChange}
          // Show included (controlled from parent for server-side filtering)
          showIncluded={showIncluded}
          onShowIncludedChange={handleShowIncludedChange}
          // Select all handlers
          onSelectAllFiltered={handleSelectAllFiltered}
          onDeselectAll={handleDeselectAll}
          isSelectingAll={isFetchingFilteredDocIds}
        />
      </div>

      <ConfirmationModal
        isOpen={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        title="Update Subset"
        onConfirm={handleSubmit}
        isLoading={isLoading}
      >
        <div className="space-y-4">
          <p className="text-gray-700">You are about to update this subset with the following changes:</p>

          <div className="ml-1 space-y-2">
            {nameHasChanged && (
              <div className="flex items-center gap-2.5 text-sm">
                <Icon className="lucide--pencil" />
                <span className="text-gray-700">
                  Name changed to: <span className="font-medium">{watchedName}</span>
                </span>
              </div>
            )}

            {issuesHaveChanged && (
              <div className="flex items-center gap-2.5 text-sm">
                <Icon className="lucide--pencil" />

                <span className="text-gray-700">Legal issues updated</span>
              </div>
            )}

            {documentChanges.added > 0 && (
              <div className="flex items-center gap-2.5 text-sm">
                <Icon className="lucide--file-plus" />
                <span>
                  <span className="font-semibold">{documentChanges.added}</span> document
                  {documentChanges.added === 1 ? '' : 's'} added
                </span>
              </div>
            )}

            {documentChanges.removed > 0 && (
              <div className="flex items-center gap-2.5 text-sm">
                <Icon className="lucide--file-minus" />
                <span>
                  <span className="font-semibold">{documentChanges.removed}</span> document
                  {documentChanges.removed === 1 ? '' : 's'} removed
                </span>
              </div>
            )}
          </div>
        </div>
      </ConfirmationModal>
    </PageContainer>
  );
}
